import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import ArrowLeft from '@crehana/icons/ArrowLeft';
import ArrowRight from '@crehana/icons/ArrowRight';
import { LANGUAGES } from '@crehana/ts-types';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';
import { useMonth } from '@datepicker-react/hooks';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { css } from 'styled-components';

import { Day } from './components';
import type { MonthProps, UseMonthResult } from './types';

const { white, primaryMain } = colors;

const Month: FC<React.PropsWithChildren<MonthProps>> = ({
  year,
  month,
  firstDayOfWeek,
  isDark,
  goToNextMonth,
  goToPreviousMonth,
}) => {
  const { languageCode } = useTranslation();

  const { days } = useMonth({
    year,
    month,
    firstDayOfWeek,
  }) as UseMonthResult;

  const firstDayOfMonth: Date = new Date(year, month, 1);
  const monthLabel = firstDayOfMonth.toLocaleDateString(languageCode, {
    month: 'long',
  });

  const dayLabels = Array.from({ length: 7 }).map((_, i) => {
    const dayOfWeek: Date = new Date(0, 0, i);
    const dayLabel = dayOfWeek.toLocaleDateString(languageCode, {
      weekday: 'narrow',
    });
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (languageCode === LANGUAGES.ES && dayLabel === 'X') return 'M';
    return dayLabel.split('.').join('');
  });

  return (
    <section className="w-full">
      <div className="flex items-center justify-between mb-16 px-16">
        <button
          type="button"
          className="focus:outline-none"
          onClick={goToPreviousMonth}
          data-testid="prev-month"
        >
          <ArrowLeft
            size={24}
            color={isDark ? white : primaryMain}
            className="cursor-pointer"
          />
        </button>
        <h4 className="font-h4 mx-16 text-center capitalize">
          {`${monthLabel} ${year}`}
        </h4>
        <button
          type="button"
          className="focus:outline-none"
          onClick={goToNextMonth}
          data-testid="next-month"
        >
          <ArrowRight
            size={24}
            color={isDark ? white : primaryMain}
            className="cursor-pointer"
          />
        </button>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          justifyContent: 'center',
        }}
      >
        {dayLabels.map((day, i) => (
          <span
            className="p-16 font-link text-gray-light text-center capitalize"
            // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
            key={`${day}-${i}`}
          >
            {day}
          </span>
        ))}
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          justify-items: center;
          grid-gap: 4px;
        `}
      >
        {days.map((day, i) => (
          <Day
            // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
            key={`${day.dayLabel}-${i}`}
            isDark={isDark}
            date={day.date}
            day={day.dayLabel}
          />
        ))}
      </div>
    </section>
  );
};

export default Month;
